import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../Header";
import api from "../../../services/api";
import {
  CONFIGURATION_ROLES,
  EMPTY_CONFIGURATION,
  createConfigurationPackage,
  normalizeConfiguration,
  validateConfigurationImport,
} from "./configurationImport";
import "./style.css";

const STEPS = [
  ["processes", "Processos utilizados"],
  ["stage-mapping", "Mapeamento das colunas"],
  ["contacts", "Contatos"],
  ["cadence", "Cadência"],
  ["qualification", "Qualificação"],
  ["handoff", "Handoff para vendas"],
  ["outcomes", "Ganho, perda e arquivamento"],
  ["temperature", "Temperatura"],
  ["team", "Equipe comercial"],
  ["origins", "Origens"],
  ["investments", "Investimentos"],
  ["goals-slas", "Metas e SLAs"],
  ["large-projects", "Grande projeto"],
  ["forecast", "Forecast"],
  ["channels", "Canais"],
  ["historical-diagnosis", "Diagnóstico histórico"],
  ["ambiguities", "Ambiguidades"],
  ["backfill", "Backfill"],
  ["validation", "Validação"],
  ["activation", "Ativação do SyncsAI"],
];
const ROLES = [
  ["first_contact_attempt", "Tentativa de primeiro contato"],
  ["first_contact_confirmed", "Primeiro contato efetuado"],
  ["second_contact", "Segundo contato"],
  ["third_contact", "Terceiro contato"],
  ["additional_contacts", "Contatos adicionais"],
  ["customer_replied", "Cliente respondeu"],
  ["qualification_started", "Início da qualificação"],
  ["qualified", "Qualificado"],
  ["disqualified", "Desqualificado"],
  ["handoff_started", "Início do handoff"],
  ["handoff_accepted", "Handoff aceito"],
  ["handoff_rejected", "Handoff rejeitado"],
  ["won", "Ganho"],
  ["lost", "Perda"],
  ["archived", "Arquivamento"],
  ["ignored", "Ignoradas"],
];

const ROLES_BY_STEP = {
  contacts: [
    "first_contact_attempt",
    "first_contact_confirmed",
    "customer_replied",
  ],
  cadence: ["second_contact", "third_contact", "additional_contacts"],
  qualification: [
    "qualification_started",
    "qualified",
    "disqualified",
  ],
  handoff: ["handoff_started", "handoff_accepted", "handoff_rejected"],
  outcomes: ["won", "lost", "archived", "ignored"],
};

const STEP_GUIDANCE = {
  processes: {
    objective: "Selecionar os pipelines que realmente representam a pré-venda.",
    importance: "Impede que produção, suporte ou pós-venda contaminem os indicadores.",
    ideal: "Somente processos comerciais confirmados, identificados por ID.",
    dashboard: "Define quais cards podem entrar nas métricas.",
  },
  "stage-mapping": {
    objective: "Relacionar cada coluna real a um acontecimento comercial.",
    importance: "Nomes parecidos não provam que duas etapas têm o mesmo significado.",
    ideal: "Cada papel confirmado por regra operacional, com ganho e perda obrigatórios.",
    dashboard: "Transforma movimentações em eventos tipados e auditáveis.",
  },
  contacts: {
    objective: "Distinguir tentativa, contato efetuado e resposta do cliente.",
    importance: "Uma tarefa concluída não prova que houve conversa.",
    ideal: "Eventos observados por integração e etapas usadas apenas como complemento.",
    dashboard: "Calcula cobertura e tempo de primeira resposta.",
  },
  cadence: {
    objective: "Definir as etapas e o número mínimo de tentativas de contato.",
    importance: "Evita chamar permanência em uma coluna de nova tentativa.",
    ideal: "Sequência operacional explícita e canais observáveis.",
    dashboard: "Calcula aderência à cadência sem duplicar contatos.",
  },
  qualification: {
    objective: "Separar início, aprovação e rejeição da qualificação.",
    importance: "Negociação e qualificação não são sinônimos em todos os processos.",
    ideal: "Critérios comerciais documentados e estados distintos.",
    dashboard: "Mede conversão de lead para oportunidade qualificada.",
  },
  handoff: {
    objective: "Identificar transferência, aceite e eventual rejeição pelo vendedor.",
    importance: "Atribuir responsável não deve ser confundido com venda.",
    ideal: "Eventos próprios para início e conclusão do handoff.",
    dashboard: "Mede volume e SLA de passagem para vendas.",
  },
  outcomes: {
    objective: "Confirmar ganho, perda, arquivamento e etapas ignoradas.",
    importance: "Resultados terminais incompatíveis distorcem conversão.",
    ideal: "Cada coluna terminal possui exatamente um significado.",
    dashboard: "Fecha os funis e alimenta forecast e calibração.",
  },
  temperature: {
    objective: "Explicar a temperatura persistida no card.",
    importance: "O contrato usa códigos canônicos, não aproximação textual.",
    ideal: "1=frio, 2=morno, 3=quente; demais sem classificação.",
    dashboard: "Agrupa pipeline e aplica probabilidades versionadas.",
  },
  team: {
    objective: "Verificar vendedores e seus supervisores comerciais diretos.",
    importance: "Vínculos de outros setores não devem aparecer como hierarquia de vendas.",
    ideal: "Cada vendedor ativo possui no máximo um supervisor comercial.",
    dashboard: "Publica snapshots de vendedor e supervisor nos eventos.",
  },
  origins: {
    objective: "Vincular textos legados somente a origens reais confirmadas.",
    importance: "Correspondência aproximada pode atribuir marketing incorretamente.",
    ideal: "ID estruturado ou alias exato, revisado e auditado.",
    dashboard: "Calcula aquisição e conversão por origem.",
  },
  investments: {
    objective: "Registrar campanhas e investimentos com período e origem.",
    importance: "Sem competência e vínculo explícito, ROI não é confiável.",
    ideal: "Campanha, valor, fonte, datas e origem estruturados.",
    dashboard: "Calcula investimento, custo por oportunidade e ROI.",
  },
  "goals-slas": {
    objective: "Versionar os limites de primeiro contato e handoff.",
    importance: "Metas sem vigência mudam o significado do histórico.",
    ideal: "Valores aprovados pela empresa e documentados na justificativa.",
    dashboard: "Classifica atendimentos dentro e fora do SLA vigente.",
  },
  "large-projects": {
    objective: "Definir o valor líquido mínimo de um grande projeto.",
    importance: "O limite varia por empresa e não pode ser inventado.",
    ideal: "Valor positivo aprovado comercialmente.",
    dashboard: "Separa oportunidades de maior impacto financeiro.",
  },
  forecast: {
    objective: "Versionar pesos e parâmetros de calibração do forecast.",
    importance: "Probabilidades fixas sem amostra devem ser publicadas como hipótese.",
    ideal: "Snapshots maduros, amostra mínima e janela maior que o horizonte.",
    dashboard: "Calcula pipeline ponderado e informa a confiança.",
  },
  channels: {
    objective: "Verificar quais canais possuem evidência estruturada.",
    importance: "Etapas do funil não substituem WhatsApp, e-mail ou telefonia.",
    ideal: "Mensagens e interações ligadas ao card por IDs confiáveis.",
    dashboard: "Separa contato observado de inferência histórica.",
  },
  "historical-diagnosis": {
    objective: "Medir o que pode ser recuperado do histórico existente.",
    importance: "O estado atual não reconstrói sozinho o caminho anterior.",
    ideal: "Transições reconhecíveis, fontes estruturadas e lacunas explícitas.",
    dashboard: "Publica cobertura histórica e limitações.",
  },
  ambiguities: {
    objective: "Revisar eventos que possuem mais de uma interpretação possível.",
    importance: "Ambiguidade não pode virar fato automaticamente.",
    ideal: "Decisão humana auditada ou permanência como indisponível.",
    dashboard: "Evita contagem silenciosa de eventos duvidosos.",
  },
  backfill: {
    objective: "Gerar eventos históricos de forma idempotente e controlada.",
    importance: "Reprocessamento não pode duplicar fatos nem alterar cards.",
    ideal: "Dry-run concluído antes da execução real.",
    dashboard: "Aumenta a cobertura histórica com modo de evidência identificado.",
  },
  validation: {
    objective: "Executar verificações críticas e avisos de qualidade.",
    importance: "Configuração válida não significa histórico completo.",
    ideal: "Sem bloqueios críticos e avisos compreendidos.",
    dashboard: "Publica prontidão, falhas e remediações.",
  },
  activation: {
    objective: "Publicar a configuração aprovada e liberar o consumo pelo SyncsAI.",
    importance: "A ativação deve manter rollback e auditoria.",
    ideal: "Configuração publicada, backfill reconciliado e limitações aceitas.",
    dashboard: "Entrega o contrato versionado sem fallback.",
  },
};

const stringify = (value) => JSON.stringify(value);
const byNewestVersion = (left, right) =>
  Number(right?.version || 0) - Number(left?.version || 0);
const latestByStatus = (versions, status) =>
  [...(versions || [])]
    .filter((item) => item.status === status)
    .sort(byNewestVersion)[0] || null;
const selectEditingVersion = (versions = []) => {
  const draft = latestByStatus(versions, "draft");
  const published = latestByStatus(versions, "published");
  if (
    draft &&
    (!published || Number(draft.version) > Number(published.version))
  ) {
    return draft;
  }
  return published || draft || [...versions].sort(byNewestVersion)[0] || null;
};
const toDateTimeLocal = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

function RolePicker({ label, options, selectedIds, onChange }) {
  const selected = options.filter((option) =>
    selectedIds.includes(Number(option.id)),
  );
  const add = (event) => {
    const id = Number(event.target.value);
    if (id && !selectedIds.includes(id)) onChange([...selectedIds, id]);
    event.target.value = "";
  };

  return (
    <div className="psai-role-picker">
      <span>{label}</span>
      <select aria-label={label} defaultValue="" onChange={add}>
        <option value="">Selecionar coluna…</option>
        {options
          .filter((option) => !selectedIds.includes(Number(option.id)))
          .map((option) => (
            <option key={option.id} value={option.id}>
              #{option.id} · {option.name} · {option.current_card_count} cards
            </option>
          ))}
      </select>
      <div className="psai-role-values">
        {!selected.length && <small>Nenhuma coluna selecionada</small>}
        {selected.map((option) => (
          <span key={option.id}>
            <b>
              #{option.id} · {option.name}
            </b>
            <button
              type="button"
              aria-label={`Remover ${option.name}`}
              onClick={() =>
                onChange(selectedIds.filter((id) => id !== Number(option.id)))
              }
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function PreSalesIntelligencePage() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(EMPTY_CONFIGURATION);
  const [savedConfig, setSavedConfig] = useState(EMPTY_CONFIGURATION);
  const [step, setStep] = useState("processes");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(null);
  const [justification, setJustification] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(toDateTimeLocal());
  const [retroactive, setRetroactive] = useState(false);
  const [run, setRun] = useState(null);
  const [issues, setIssues] = useState([]);
  const [originChoices, setOriginChoices] = useState({});
  const [investment, setInvestment] = useState({
    descricao: "",
    valor: "",
    originId: "",
    dataInicial: "",
    dataFinal: "",
    allocationMethod: "period_prorated",
    monthlyAmount: "",
    observacoes: "",
  });
  const [campaign, setCampaign] = useState({
    name: "",
    channel: "",
    origin_id: "",
    starts_at: "",
    ends_at: "",
  });
  const [campaignInvestment, setCampaignInvestment] = useState({
    campaign_id: "",
    amount: "",
    starts_on: "",
    ends_on: "",
    source: "",
  });
  const [cardAttribution, setCardAttribution] = useState({
    card_id: "",
    campaign_id: "",
    origin_id: "",
    justification: "",
  });
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importResult, setImportResult] = useState(null);
  const stop = useRef(false);
  const mainRef = useRef(null);
  const configRef = useRef(config);
  const savedConfigRef = useRef(savedConfig);
  const hasUnsavedChanges =
    stringify(config) !== stringify(savedConfig);

  useEffect(() => {
    configRef.current = config;
  }, [config]);
  useEffect(() => {
    savedConfigRef.current = savedConfig;
  }, [savedConfig]);
  useEffect(() => {
    setPreview(null);
  }, [config, effectiveFrom, retroactive]);
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasUnsavedChanges]);

  const load = async ({ preserveDraft = false } = {}) => {
    setBusy("load");
    try {
      const response = await api.get("/pre-sales-intelligence/bootstrap");
      setData(response.data);
      const versions = response.data.configuration_versions || [];
      const selected = selectEditingVersion(versions);
      const serverConfiguration = normalizeConfiguration(
        selected?.configuration,
      );
      if (
        !preserveDraft ||
        stringify(configRef.current) === stringify(savedConfigRef.current)
      ) {
        setConfig(serverConfiguration);
        configRef.current = serverConfiguration;
      }
      setSavedConfig(serverConfiguration);
      savedConfigRef.current = serverConfiguration;
      setRun(response.data.backfill_runs?.[0] || null);
    } catch (error) {
      setNotice([
        "error",
        error.response?.data?.message ||
          "Falha ao carregar. A migration foi aplicada?",
      ]);
    } finally {
      setBusy("");
    }
  };
  useEffect(() => {
    load({ preserveDraft: false });
    // A carga inicial deve ocorrer uma única vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const catalog = data?.catalog || {};
  const health = data?.health || {};
  const checks = health.checks || [];
  const settings = health.settings || {};
  const critical = checks.filter(
    (item) => item.severity === "critical" && item.result === "fail",
  );
  const warnings = checks.filter(
    (item) => item.severity === "warning" && item.result === "fail",
  );
  const columns = useMemo(
    () =>
      (catalog.columns || []).filter(
        (column) =>
          !search ||
          `${column.id} ${column.name} ${column.process_name || ""}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [catalog.columns, search],
  );
  const processes = useMemo(() => {
    const list = (catalog.boards || []).map((item) => ({
      id: Number(item.id),
      name: item.name,
    }));
    if ((catalog.columns || []).some((item) => item.board_id === null))
      list.push({ id: null, name: "Processo legado sem board" });
    return list;
  }, [catalog.boards, catalog.columns]);
  const selectedProcess = (id) =>
    config.processes.find((item) => (item.process_id ?? null) === (id ?? null));
  const toggleProcess = (id) =>
    setConfig((current) => ({
      ...current,
      processes: current.processes.some(
        (item) => (item.process_id ?? null) === (id ?? null),
      )
        ? current.processes.filter(
            (item) => (item.process_id ?? null) !== (id ?? null),
          )
        : [...current.processes, { process_id: id, roles: {} }],
    }));
  const setRole = (processId, role, ids) =>
    setConfig((current) => ({
      ...current,
      processes: current.processes.map((process) =>
        (process.process_id ?? null) === (processId ?? null)
          ? {
              ...process,
              roles: {
                ...(process.roles || {}),
                [role]: Array.from(
                  new Set(ids.map(Number).filter(Number.isInteger)),
                ),
              },
            }
          : process,
      ),
    }));
  const fail = (error) => {
    const payload = error.response?.data;
    const message =
      typeof payload?.message === "string"
        ? payload.message
        : Array.isArray(payload?.message)
          ? payload.message.join(" ")
          : typeof payload === "string"
            ? payload
            : error.message || "Não foi possível concluir a operação.";
    const details = payload?.problems || payload?.failures || payload?.warnings;
    setNotice([
      "error",
      Array.isArray(details) && details.length
        ? `${message} ${details
            .slice(0, 3)
            .map((item) => item.description || item.title || item.code)
            .join(" ")}`
        : message,
    ]);
  };
  const action = async (
    name,
    callback,
    success,
    reload = true,
    preserveDraft = true,
  ) => {
    setBusy(name);
    try {
      const result = await callback();
      setNotice(["success", success]);
      if (reload) await load({ preserveDraft });
      return result;
    } catch (error) {
      fail(error);
    } finally {
      setBusy("");
    }
  };
  const previewConfig = () =>
    action(
      "preview",
      async () => {
        const response = await api.post(
          "/pre-sales-intelligence/configurations/preview",
          {
            configuration: config,
            effective_from: new Date(effectiveFrom).toISOString(),
            retroactive_authorized: retroactive,
          },
        );
        setPreview(response.data);
        return response.data;
      },
      "Prévia calculada.",
      false,
    );
  const save = async () => {
    if (!justification.trim()) {
      setNotice(["warning", "Informe a justificativa."]);
      return;
    }
    setBusy("save");
    try {
      const effectiveDate = new Date(effectiveFrom);
      if (Number.isNaN(effectiveDate.getTime())) {
        setNotice(["error", "Informe uma vigência válida."]);
        return;
      }
      const validationResponse = await api.post(
        "/pre-sales-intelligence/configurations/preview",
        {
          configuration: config,
          effective_from: effectiveDate.toISOString(),
          retroactive_authorized: retroactive,
        },
      );
      const validation = validationResponse.data;
      setPreview(validation);
      if (!validation.valid) {
        setNotice([
          "error",
          "A versão não foi salva porque a prévia encontrou erros críticos. Corrija os itens destacados.",
        ]);
        return;
      }
      const response = await api.post(
        "/pre-sales-intelligence/configurations",
        {
          configuration: validation.normalized,
          justification,
          effective_from: effectiveDate.toISOString(),
          retroactive_authorized: retroactive,
        },
      );
      const persisted = normalizeConfiguration(response.data.configuration);
      setConfig(persisted);
      setSavedConfig(persisted);
      configRef.current = persisted;
      savedConfigRef.current = persisted;
      setNotice(["success", "Nova versão salva como rascunho."]);
      await load({ preserveDraft: false });
    } catch (error) {
      fail(error);
    } finally {
      setBusy("");
    }
  };
  const publish = () => {
    if (hasUnsavedChanges) {
      return setNotice([
        "warning",
        "Existem alterações não salvas. Salve a versão antes de publicar.",
      ]);
    }
    const versions = data?.configuration_versions || [];
    const published = latestByStatus(versions, "published");
    const draft = [...versions]
      .filter(
        (item) =>
          item.status === "draft" &&
          (!published || Number(item.version) > Number(published.version)),
      )
      .sort(byNewestVersion)[0];
    if (!draft) return setNotice(["warning", "Salve um rascunho primeiro."]);
    if (window.confirm(`Publicar a versão ${draft.version}?`))
      action(
        "publish",
        () =>
          api.post(
            `/pre-sales-intelligence/configurations/${draft.id}/publish`,
            {
              confirm: true,
              justification: justification || draft.justification,
            },
          ),
        `Versão ${draft.version} publicada.`,
      );
  };
  const validateImport = () => {
    const result = validateConfigurationImport(importText, catalog);
    setImportResult(result);
    setNotice([
      result.valid ? "success" : "error",
      result.valid
        ? "JSON validado localmente. A prévia oficial do servidor ainda será exigida antes de salvar."
        : `A importação possui ${result.errors.length} erro(s). Nada foi aplicado.`,
    ]);
  };
  const applyImport = () => {
    if (!importResult?.valid) {
      setNotice(["warning", "Valide um JSON sem erros antes de aplicar."]);
      return;
    }
    const imported = normalizeConfiguration(importResult.configuration);
    setConfig(imported);
    configRef.current = imported;
    setPreview(null);
    if (importResult.metadata?.justification) {
      setJustification(importResult.metadata.justification);
    }
    if (importResult.metadata?.effective_from) {
      const localDate = toDateTimeLocal(importResult.metadata.effective_from);
      if (localDate) setEffectiveFrom(localDate);
    }
    setRetroactive(
      Boolean(importResult.metadata?.retroactive_authorized),
    );
    setNotice([
      "success",
      "JSON aplicado somente ao formulário. Revise a prévia e salve para persistir.",
    ]);
  };
  const exportConfiguration = async () => {
    const text = createConfigurationPackage({
      configuration: config,
      justification,
      effectiveFrom: effectiveFrom
        ? new Date(effectiveFrom).toISOString()
        : "",
      retroactiveAuthorized: retroactive,
    });
    setImportText(text);
    setImportResult(validateConfigurationImport(text, catalog));
    setShowImport(true);
    try {
      await navigator.clipboard.writeText(text);
      setNotice(["success", "JSON atual copiado e aberto para conferência."]);
    } catch {
      setNotice([
        "success",
        "JSON atual aberto para conferência. Copie o conteúdo do campo.",
      ]);
    }
  };
  const setCapture = () => {
    const enabled = !settings.capture_enabled;
    const reason = window.prompt(
      `Justificativa para ${enabled ? "habilitar" : "desabilitar"} a captura:`,
    );
    if (reason)
      action(
        "capture",
        () =>
          api.put("/pre-sales-intelligence/feature-flags/capture", {
            enabled,
            justification: reason,
          }),
        "Feature flag atualizada.",
      );
  };
  const createRun = (dry) =>
    action(
      dry ? "create-dry-run" : "create-backfill",
      async () => {
        const response = await api.post(
          "/pre-sales-intelligence/backfill-runs",
          { dry_run: dry, batch_size: 1000 },
        );
        setRun(response.data.run);
        return response;
      },
      `${dry ? "Dry-run" : "Backfill"} criado.`,
    );
  const resumeRun = () => {
    if (!run || run.status !== "failed") return;
    return action(
      "resume-run",
      async () => {
        const response = await api.post(
          `/pre-sales-intelligence/backfill-runs/${run.id}/resume`,
        );
        setRun(response.data);
        return response;
      },
      "Execução retomada. Processe os lotes para continuar.",
      false,
    );
  };
  const processRun = async () => {
    if (!run) return;
    stop.current = false;
    setBusy("process");
    try {
      let complete = false,
        current = run;
      while (!complete && !stop.current) {
        const response = await api.post(
          `/pre-sales-intelligence/backfill-runs/${current.id}/process-next-batch`,
        );
        current = response.data.run;
        complete = response.data.complete;
        setRun(current);
      }
      setNotice([
        complete ? "success" : "warning",
        complete
          ? "Execução concluída e reconciliada."
          : "Pausa segura após o lote.",
      ]);
      await load({ preserveDraft: true });
    } catch (error) {
      fail(error);
      await load({ preserveDraft: true });
    } finally {
      setBusy("");
    }
  };
  const loadIssues = async () => {
    if (!run) return;
    const response = await api.get(
      `/pre-sales-intelligence/backfill-runs/${run.id}/issues`,
      { params: { status: "pending", page_size: 100 } },
    );
    setIssues(response.data.items || []);
  };
  const activate = () => {
    const codes = warnings.map((item) => item.check_code);
    const reason = window.prompt(
      `Avisos aceitos: ${codes.join(", ") || "nenhum"}.\nJustificativa:`,
    );
    if (reason)
      action(
        "activate",
        () =>
          api.post("/pre-sales-intelligence/activation", {
            confirm: true,
            warning_codes: codes,
            justification: reason,
          }),
        "SyncsAI ativado.",
      );
  };
  const reviewOrigin = (item, status) => {
    const selected = Number(originChoices[item.legacy_origin_text]) || null;
    if (status === "linked" && !selected) {
      return setNotice([
        "warning",
        "Selecione a origem real antes de confirmar o alias.",
      ]);
    }
    return action(
      "origin-review",
      () =>
        api.put("/pre-sales-intelligence/origin-links/review", {
          legacy_origin_text: item.legacy_origin_text,
          selected_origin_id: selected,
          status,
        }),
      status === "linked"
        ? `${item.card_count} cards passaram a usar um alias explícito, sem fuzzy matching.`
        : `O texto "${item.legacy_origin_text}" foi descartado como origem real; nenhum card foi excluído.`,
    );
  };
  const saveInvestment = () =>
    action(
      "investment",
      () =>
        api.post("/marketing/investimentos", {
          ...investment,
          valor: Number(investment.valor),
          originId: Number(investment.originId) || null,
          monthlyAmount:
            investment.monthlyAmount === ""
              ? null
              : Number(investment.monthlyAmount),
          origem:
            (catalog.origins || []).find(
              (item) => Number(item.id) === Number(investment.originId),
            )?.name || "",
        }),
      "Investimento salvo e prontidão marcada para nova verificação.",
    );
  const deleteInvestment = (id) => {
    if (window.confirm("Excluir este investimento desta empresa?")) {
      action(
        "investment-delete",
        () => api.delete(`/marketing/investimentos/${id}`),
        "Investimento excluído.",
      );
    }
  };
  const saveCampaign = () =>
    action(
      "campaign",
      () =>
        api.post("/marketing/campanhas", {
          ...campaign,
          origin_id: Number(campaign.origin_id) || null,
        }),
      "Campanha cadastrada com vínculo explícito.",
    );
  const saveCampaignInvestment = () =>
    action(
      "campaign-investment",
      () =>
        api.post("/marketing/campanhas/investimentos", {
          ...campaignInvestment,
          campaign_id: Number(campaignInvestment.campaign_id),
          amount: Number(campaignInvestment.amount),
        }),
      "Investimento vinculado à campanha e ao período.",
    );
  const deleteCampaignInvestment = (id) => {
    if (window.confirm("Excluir este investimento de campanha?")) {
      action(
        "campaign-investment-delete",
        () => api.delete(`/marketing/campanhas/investimentos/${id}`),
        "Investimento de campanha excluído.",
      );
    }
  };
  const saveCardAttribution = () =>
    action(
      "card-attribution",
      () =>
        api.put(
          `/marketing/cards/${Number(cardAttribution.card_id)}/attribution`,
          {
            campaign_id: Number(cardAttribution.campaign_id) || null,
            origin_id: Number(cardAttribution.origin_id) || null,
            justification: cardAttribution.justification,
          },
        ),
      "Atribuição explícita salva com auditoria.",
    );
  if (!data)
    return (
      <>
        <Header />
        <main className="psai-page">
          <div className="psai-loading">
            {busy ? "Carregando diagnóstico…" : notice?.[1]}
          </div>
        </main>
      </>
    );

  const diagnostic = data.historical_diagnostic || {};
  const configSteps = [
    "processes",
    "stage-mapping",
    "contacts",
    "cadence",
    "qualification",
    "handoff",
    "outcomes",
  ];
  const historySteps = [
    "historical-diagnosis",
    "ambiguities",
    "backfill",
    "validation",
  ];
  const visibleRoleNames =
    step === "stage-mapping"
      ? CONFIGURATION_ROLES
      : ROLES_BY_STEP[step] || [];
  const visibleRoles = ROLES.filter(([role]) =>
    visibleRoleNames.includes(role),
  );
  const editingVersion = selectEditingVersion(
    data.configuration_versions || [],
  );
  const processableRunStatuses = [
    "pending",
    "dry_running",
    "running",
    "paused",
  ];
  const runIsActive = processableRunStatuses.includes(run?.status);
  const runTotal = Number(
    data?.historical_diagnostic?.summary?.history_records || 1,
  );
  const runAnalyzed = Number(run?.analyzed_count || 0);
  const runPercentage = Math.min(
    100,
    Math.max(0, (runAnalyzed / runTotal) * 100),
  );
  const guidance = STEP_GUIDANCE[step] || STEP_GUIDANCE.processes;
  return (
    <>
      <Header />
      <main className="psai-page">
        <header className="psai-hero">
          <div>
            <small>Configurações › Inteligência de Pré-venda</small>
            <h1>Dados confiáveis para o SyncsAI</h1>
            <p>
              Fatos observados, inferências e lacunas históricas aparecem
              separadamente.
            </p>
          </div>
          <div
            className={critical.length ? "psai-score blocked" : "psai-score"}
          >
            <b>
              {Number(health.readiness?.overall_percentage || 0).toFixed(0)}%
            </b>
            <span>prontidão</span>
          </div>
        </header>
        {notice && (
          <div className={`psai-notice ${notice[0]}`}>
            {notice[1]}
            <button onClick={() => setNotice(null)}>×</button>
          </div>
        )}
        {hasUnsavedChanges && (
          <div className="psai-dirty">
            <div>
              <b>Alterações ainda não salvas</b>
              <span>
                Elas permanecem ao navegar e ao atualizar diagnósticos, mas só
                entram no banco depois de salvar uma versão.
              </span>
            </div>
            <button
              className="secondary"
              onClick={() => {
                const restored = normalizeConfiguration(savedConfig);
                setConfig(restored);
                configRef.current = restored;
                setPreview(null);
                setNotice(["success", "Alterações locais descartadas."]);
              }}
            >
              Descartar alterações locais
            </button>
          </div>
        )}
        <section className="psai-health">
          <article>
            <span>Erros críticos</span>
            <b>{critical.length}</b>
            <small>bloqueiam ativação</small>
          </article>
          <article>
            <span>Avisos</span>
            <b>{warnings.length}</b>
            <small>exigem aceite</small>
          </article>
          <article>
            <span>Último evento</span>
            <b className="small">
              {health.events?.last_event_at
                ? new Date(health.events.last_event_at).toLocaleString("pt-BR")
                : "Sem eventos"}
            </b>
            <small>{health.events?.covered_cards || 0} cards</small>
          </article>
          <article>
            <span>Captura</span>
            <b className="small">
              {settings.capture_enabled ? "Shadow-write ativo" : "Desativada"}
            </b>
            <small>por empresa</small>
          </article>
          <article>
            <span>SyncsAI</span>
            <b className="small">
              {settings.syncsai_enabled ? "Ativo" : "Desativado"}
            </b>
            <small>rollback por flag</small>
          </article>
        </section>
        <section className="psai-import-panel">
          <div className="psai-import-heading">
            <div>
              <small>Configuração em lote</small>
              <h2>Importar ou exportar JSON</h2>
              <p>
                O JSON configura todos os campos versionados sem escolher
                tenant. Nada é gravado antes da prévia e do salvamento.
              </p>
            </div>
            <div className="psai-actions">
              <button className="secondary" onClick={exportConfiguration}>
                Exportar configuração atual
              </button>
              <button onClick={() => setShowImport((current) => !current)}>
                {showImport ? "Fechar importação" : "Importar JSON"}
              </button>
            </div>
          </div>
          {showImport && (
            <div className="psai-import-body">
              <label>
                Cole o pacote JSON
                <textarea
                  value={importText}
                  spellCheck="false"
                  placeholder='{"schema_version":"1.0.0","configuration":{...}}'
                  onChange={(event) => {
                    setImportText(event.target.value);
                    setImportResult(null);
                  }}
                />
              </label>
              <div className="psai-actions">
                <button className="secondary" onClick={validateImport}>
                  1. Validar JSON
                </button>
                <button
                  disabled={!importResult?.valid}
                  onClick={applyImport}
                >
                  2. Aplicar ao formulário
                </button>
              </div>
              {importResult && (
                <div
                  className={`psai-import-result ${
                    importResult.valid ? "valid" : "invalid"
                  }`}
                >
                  <b>
                    {importResult.valid
                      ? "JSON pronto para aplicar"
                      : "JSON bloqueado"}
                  </b>
                  {importResult.summary && (
                    <span>
                      {importResult.summary.processes} processo(s) ·{" "}
                      {importResult.summary.mapped_roles} papel(is) ·{" "}
                      {importResult.summary.mapped_columns} coluna(s)
                    </span>
                  )}
                  {importResult.errors.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  {importResult.warnings.map((item) => (
                    <p key={item} className="warning">
                      {item}
                    </p>
                  ))}
                </div>
              )}
              <p className="note">
                Aliases de origem, campanhas, investimentos e backfills têm
                auditoria própria e não são alterados por esta importação.
              </p>
            </div>
          )}
        </section>
        <div className="psai-layout">
          <nav className="psai-steps">
            {STEPS.map(([code, title], index) => (
              <button
                key={code}
                className={step === code ? "active" : ""}
                onClick={() => {
                  setStep(code);
                  setSearch("");
                  window.requestAnimationFrame(() =>
                    mainRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    }),
                  );
                }}
              >
                <i>{index + 1}</i>
                <span>{title}</span>
              </button>
            ))}
          </nav>
          <section className="psai-main" ref={mainRef}>
            <div className="psai-heading">
              <div>
                <small>
                  Etapa {STEPS.findIndex(([code]) => code === step) + 1}
                </small>
                <h2>{STEPS.find(([code]) => code === step)?.[1]}</h2>
                <span className="psai-version-status">
                  {editingVersion
                    ? `Editando versão ${editingVersion.version} · ${editingVersion.status}`
                    : "Primeira configuração"}
                </span>
              </div>
              <button
                className="secondary"
                onClick={() =>
                  action(
                    "readiness",
                    () => api.post("/pre-sales-intelligence/readiness/run"),
                    "Verificações atualizadas.",
                  )
                }
              >
                Verificar novamente
              </button>
            </div>
            <div className="psai-explain">
              <div>
                <b>Objetivo</b>
                <p>{guidance.objective}</p>
              </div>
              <div>
                <b>Importância</b>
                <p>{guidance.importance}</p>
              </div>
              <div>
                <b>Cenário ideal</b>
                <p>{guidance.ideal}</p>
              </div>
              <div>
                <b>Dashboard</b>
                <p>{guidance.dashboard}</p>
              </div>
            </div>
            {step === "processes" && (
              <article className="psai-panel">
                <h3>Processos reais</h3>
                <p>
                  Selecione somente os processos que representam a pré-venda.
                  A configuração guarda o ID do processo, nunca apenas o nome.
                </p>
                <div className="psai-processes">
                  {processes.map((process) => (
                    <label key={process.id ?? "legacy"}>
                      <input
                        type="checkbox"
                        checked={!!selectedProcess(process.id)}
                        onChange={() => toggleProcess(process.id)}
                      />
                      {process.name}
                      <small>ID {process.id ?? "ausente"}</small>
                    </label>
                  ))}
                </div>
              </article>
            )}
            {configSteps.includes(step) && step !== "processes" && (
              <>
                <article className="psai-panel">
                  <div className="psai-title">
                    <div>
                      <h3>
                        {step === "stage-mapping"
                          ? "Todos os papéis das colunas"
                          : "Papéis desta etapa"}
                      </h3>
                      <p>
                        Selecione normalmente; cada escolha vira uma etiqueta
                        removível. Não é necessário usar Ctrl.
                      </p>
                    </div>
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar coluna ou ID"
                    />
                  </div>
                  {!config.processes.length && (
                    <p className="note">
                      Primeiro selecione um processo na etapa 1.
                    </p>
                  )}
                  {config.processes.map((process) => (
                    <section
                      className="psai-mapping"
                      key={process.process_id ?? "legacy"}
                    >
                      <h4>
                        {
                          processes.find(
                            (item) =>
                              (item.id ?? null) ===
                              (process.process_id ?? null),
                          )?.name
                        }
                      </h4>
                      <div className="psai-role-grid">
                        {visibleRoles.map(([role, label]) => (
                          <RolePicker
                            key={role}
                            label={label}
                            options={columns.filter(
                              (column) =>
                                (column.board_id ?? null) ===
                                (process.process_id ?? null),
                            )}
                            selectedIds={process.roles?.[role] || []}
                            onChange={(ids) =>
                              setRole(process.process_id, role, ids)
                            }
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </article>
                {step === "stage-mapping" && (
                  <article className="psai-panel">
                  <h3>Sugestões (confirmação obrigatória)</h3>
                  <p>
                    Sugestões usam somente o texto da coluna. Confirme apenas
                    quando a regra operacional for verdadeira.
                  </p>
                  <div className="psai-suggestions">
                    {(catalog.suggestions || [])
                      .slice(0, 30)
                      .map((suggestion, index) => (
                        <button
                          key={`${suggestion.column_id}-${index}`}
                          onClick={() => {
                            if (!window.confirm(suggestion.reason)) return;
                            setConfig((current) => {
                              const list = [...current.processes];
                              let position = list.findIndex(
                                (item) =>
                                  (item.process_id ?? null) ===
                                  (suggestion.process_id ?? null),
                              );
                              if (position < 0) {
                                list.push({
                                  process_id: suggestion.process_id,
                                  roles: {},
                                });
                                position = list.length - 1;
                              }
                              const item = list[position];
                              list[position] = {
                                ...item,
                                roles: {
                                  ...item.roles,
                                  [suggestion.role]: [
                                    ...new Set([
                                      ...(item.roles?.[suggestion.role] || []),
                                      suggestion.column_id,
                                    ]),
                                  ],
                                },
                              };
                              return { ...current, processes: list };
                            });
                          }}
                        >
                          <b>{suggestion.column_name}</b>
                          <span>→ {suggestion.role.replaceAll("_", " ")}</span>
                        </button>
                      ))}
                  </div>
                  </article>
                )}
              </>
            )}
            {step === "cadence" && (
              <article className="psai-panel psai-fields">
                <h3>Regra de cadência</h3>
                <label>
                  Tentativas mínimas
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={config.cadence.minimum_attempts ?? ""}
                    onChange={(event) =>
                      setConfig({
                        ...config,
                        cadence: {
                          ...config.cadence,
                          minimum_attempts: Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
              </article>
            )}
            {step === "temperature" && (
              <article className="psai-panel">
                <h3>Temperatura canônica do CRM</h3>
                <p>
                  A temperatura vem de <code>cards.temperatura_card</code>. Os
                  códigos abaixo são fixos no contrato e não exigem seleção
                  manual.
                </p>
                <div className="psai-temperature-map">
                  <span>
                    <b>1</b> Frio
                  </span>
                  <span>
                    <b>2</b> Morno
                  </span>
                  <span>
                    <b>3</b> Quente
                  </span>
                  <span>
                    <b>Outros/nulo</b> Sem temperatura
                  </span>
                </div>
              </article>
            )}
            {step === "goals-slas" && (
              <article className="psai-panel psai-fields">
                <h3>SLAs versionados</h3>
                <label>
                  SLA primeiro contato (h)
                  <input
                    type="number"
                    min="1"
                    value={config.slas.first_contact_hours || ""}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        slas: {
                          ...config.slas,
                          first_contact_hours: Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label>
                  SLA handoff (h)
                  <input
                    type="number"
                    min="1"
                    value={config.slas.handoff_hours || ""}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        slas: {
                          ...config.slas,
                          handoff_hours: Number(e.target.value),
                        },
                      })
                    }
                  />
                </label>
              </article>
            )}
            {step === "large-projects" && (
              <article className="psai-panel psai-fields">
                <h3>Definição de grande projeto</h3>
                <label>
                  Grande projeto (R$)
                  <input
                    type="number"
                    min="0"
                    value={config.large_project.threshold || ""}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        large_project: {
                          threshold: Number(e.target.value) || null,
                        },
                      })
                    }
                  />
                </label>
                <p className="note">
                  Informe o valor líquido mínimo aprovado pela empresa. Esse
                  limite afeta somente a classificação analítica.
                </p>
              </article>
            )}
            {step === "channels" && (
              <article className="psai-panel">
                <h3>Canais observáveis</h3>
                <p>
                  Esta etapa é somente diagnóstico. Canais são detectados nas
                  integrações estruturadas; não é seguro inventá-los
                  manualmente.
                </p>
                <div className="psai-metrics">
                  <div>
                    <b>
                      {diagnostic.structured_sources
                        ?.linked_whatsapp_messages || 0}
                    </b>
                    <span>mensagens WhatsApp vinculadas</span>
                  </div>
                  <div>
                    <b>
                      {diagnostic.structured_sources?.card_interactions || 0}
                    </b>
                    <span>interações estruturadas</span>
                  </div>
                </div>
              </article>
            )}
            {step === "forecast" && (
              <article className="psai-panel psai-fields">
                <h3>Calibração auditável</h3>
                {[
                  ["cold_probability", "Probabilidade frio", 0.01],
                  ["warm_probability", "Probabilidade morno", 0.01],
                  ["hot_probability", "Probabilidade quente", 0.01],
                  [
                    "unclassified_probability",
                    "Probabilidade sem temperatura",
                    0.01,
                  ],
                ].map(([field, label, increment]) => (
                  <label key={field}>
                    {label}
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step={increment}
                      value={config.forecast[field] ?? ""}
                      onChange={(event) =>
                        setConfig({
                          ...config,
                          forecast: {
                            ...config.forecast,
                            [field]: Number(event.target.value),
                          },
                        })
                      }
                    />
                  </label>
                ))}
                {[
                  ["minimum_sample_size", "Amostra mínima"],
                  ["horizon_days", "Horizonte (dias)"],
                  ["history_window_days", "Janela histórica (dias)"],
                ].map(([field, label]) => (
                  <label key={field}>
                    {label}
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={config.forecast[field] ?? ""}
                      onChange={(event) =>
                        setConfig({
                          ...config,
                          forecast: {
                            ...config.forecast,
                            [field]: Number(event.target.value),
                          },
                        })
                      }
                    />
                  </label>
                ))}
                <p className="note">
                  A calibração só é publicada quando snapshots maduros atingem a
                  amostra mínima aprovada. A janela histórica deve ser maior que
                  o horizonte.
                </p>
              </article>
            )}
            {step === "team" && (
              <article className="psai-panel">
                <h3>Hierarquia atual</h3>
                <div className="psai-table">
                  {(catalog.users || [])
                    .filter((item) => item.is_active)
                    .map((item) => (
                      <div key={item.id}>
                        <span>
                          {item.username} <small>#{item.id}</small>
                        </span>
                        <span>{item.supervisor_name || "Sem supervisor"}</span>
                        <b
                          className={
                            item.supervisor_matches === 1 ? "ok" : "bad"
                          }
                        >
                          {item.supervisor_matches === 1
                            ? "Único"
                            : item.supervisor_matches > 1
                              ? "Ambíguo"
                              : "Ausente"}
                        </b>
                      </div>
                    ))}
                </div>
                <p className="note">
                  Sem vigência histórica, a hierarquia atual não será
                  apresentada como histórica. Novos eventos salvam snapshots.
                </p>
              </article>
            )}
            {step === "origins" && (
              <article className="psai-panel">
                <h3>Origens reais e aliases explícitos</h3>
                <div className="psai-table">
                  {(catalog.origins || []).map((item) => (
                    <div key={item.id}>
                      <span>{item.name}</span>
                      <span>{item.card_count} cards</span>
                      <b>#{item.id}</b>
                    </div>
                  ))}
                </div>
                <h4>Textos legados sem vínculo</h4>
                {!(catalog.unmatched_origins || []).length && (
                  <p className="note">
                    Todos os textos utilizados possuem vínculo exato ou alias
                    confirmado.
                  </p>
                )}
                {(catalog.unmatched_origins || []).map((item) => (
                  <div
                    className="psai-origin-review"
                    key={item.legacy_origin_text}
                  >
                    <div>
                      <b>{item.legacy_origin_text}</b>
                      <small>
                        {item.card_count} cards ·{" "}
                        {item.review_status || "pendente"}
                      </small>
                    </div>
                    <select
                      value={originChoices[item.legacy_origin_text] || ""}
                      onChange={(event) =>
                        setOriginChoices({
                          ...originChoices,
                          [item.legacy_origin_text]: event.target.value,
                        })
                      }
                    >
                      <option value="">Selecionar origem real</option>
                      {(catalog.origins || []).map((origin) => (
                        <option key={origin.id} value={origin.id}>
                          #{origin.id} · {origin.name}
                        </option>
                      ))}
                    </select>
                    <button onClick={() => reviewOrigin(item, "linked")}>
                      Confirmar alias
                    </button>
                    <button
                      className="secondary"
                      onClick={() => reviewOrigin(item, "dismissed")}
                    >
                      Descartar
                    </button>
                  </div>
                ))}
                <p className="note">
                  Nenhuma aproximação textual é aplicada silenciosamente. O
                  vínculo guarda usuário, data e quantidade afetada.
                </p>
              </article>
            )}
            {step === "investments" && (
              <article className="psai-panel">
                <h3>Campanhas auditáveis</h3>
                <div className="psai-fields">
                  <label>
                    Nome da campanha
                    <input
                      value={campaign.name}
                      onChange={(event) =>
                        setCampaign({ ...campaign, name: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Canal
                    <input
                      value={campaign.channel}
                      onChange={(event) =>
                        setCampaign({
                          ...campaign,
                          channel: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Origem
                    <select
                      value={campaign.origin_id}
                      onChange={(event) =>
                        setCampaign({
                          ...campaign,
                          origin_id: event.target.value,
                        })
                      }
                    >
                      <option value="">Sem origem vinculada</option>
                      {(catalog.origins || []).map((origin) => (
                        <option key={origin.id} value={origin.id}>
                          #{origin.id} · {origin.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Início
                    <input
                      type="date"
                      value={campaign.starts_at}
                      onChange={(event) =>
                        setCampaign({
                          ...campaign,
                          starts_at: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Fim
                    <input
                      type="date"
                      value={campaign.ends_at}
                      onChange={(event) =>
                        setCampaign({
                          ...campaign,
                          ends_at: event.target.value,
                        })
                      }
                    />
                  </label>
                  <button onClick={saveCampaign}>Adicionar campanha</button>
                </div>
                <div className="psai-table">
                  {(catalog.campaigns || []).map((item) => (
                    <div key={item.id}>
                      <span>
                        {item.name}
                        <small>
                          {item.starts_at || "sem início"} a{" "}
                          {item.ends_at || "sem fim"}
                        </small>
                      </span>
                      <span>{item.channel || "Sem canal"}</span>
                      <b>{item.attributed_card_count || 0} cards</b>
                    </div>
                  ))}
                </div>

                <h3>Atribuir card existente</h3>
                <div className="psai-fields">
                  <label>
                    ID do card
                    <input
                      type="number"
                      min="1"
                      value={cardAttribution.card_id}
                      onChange={(event) =>
                        setCardAttribution({
                          ...cardAttribution,
                          card_id: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Campanha
                    <select
                      value={cardAttribution.campaign_id}
                      onChange={(event) =>
                        setCardAttribution({
                          ...cardAttribution,
                          campaign_id: event.target.value,
                        })
                      }
                    >
                      <option value="">Não alterar campanha</option>
                      {(catalog.campaigns || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          #{item.id} · {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Origem
                    <select
                      value={cardAttribution.origin_id}
                      onChange={(event) =>
                        setCardAttribution({
                          ...cardAttribution,
                          origin_id: event.target.value,
                        })
                      }
                    >
                      <option value="">Usar origem da campanha</option>
                      {(catalog.origins || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          #{item.id} · {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Justificativa
                    <input
                      value={cardAttribution.justification}
                      onChange={(event) =>
                        setCardAttribution({
                          ...cardAttribution,
                          justification: event.target.value,
                        })
                      }
                    />
                  </label>
                  <button onClick={saveCardAttribution}>
                    Salvar atribuição
                  </button>
                </div>
                <p className="note">
                  Atribuições são feitas apenas por IDs do mesmo tenant e
                  registram antes/depois. Nenhum texto semelhante vira campanha
                  automaticamente.
                </p>

                <h3>Investimento por campanha</h3>
                <div className="psai-fields">
                  <label>
                    Campanha
                    <select
                      value={campaignInvestment.campaign_id}
                      onChange={(event) =>
                        setCampaignInvestment({
                          ...campaignInvestment,
                          campaign_id: event.target.value,
                        })
                      }
                    >
                      <option value="">Selecione</option>
                      {(catalog.campaigns || [])
                        .filter((item) => item.active)
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            #{item.id} · {item.name}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Valor
                    <input
                      type="number"
                      min="0"
                      value={campaignInvestment.amount}
                      onChange={(event) =>
                        setCampaignInvestment({
                          ...campaignInvestment,
                          amount: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Início
                    <input
                      type="date"
                      value={campaignInvestment.starts_on}
                      onChange={(event) =>
                        setCampaignInvestment({
                          ...campaignInvestment,
                          starts_on: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Fim
                    <input
                      type="date"
                      value={campaignInvestment.ends_on}
                      onChange={(event) =>
                        setCampaignInvestment({
                          ...campaignInvestment,
                          ends_on: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Fonte
                    <input
                      value={campaignInvestment.source}
                      placeholder="nota, plataforma, planilha..."
                      onChange={(event) =>
                        setCampaignInvestment({
                          ...campaignInvestment,
                          source: event.target.value,
                        })
                      }
                    />
                  </label>
                  <button onClick={saveCampaignInvestment}>
                    Vincular investimento
                  </button>
                </div>
                <div className="psai-table">
                  {(catalog.campaign_investments || []).map((item) => (
                    <div key={item.id}>
                      <span>
                        {item.campaign_name}
                        <small>
                          {item.starts_on} a {item.ends_on}
                        </small>
                      </span>
                      <span>
                        R${" "}
                        {Number(item.amount || 0).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      <b>{item.source}</b>
                      <button
                        className="danger"
                        onClick={() => deleteCampaignInvestment(item.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  ))}
                </div>

                <h3>Investimentos por origem e competência</h3>
                <div className="psai-fields">
                  <label>
                    Descrição
                    <input
                      value={investment.descricao}
                      onChange={(e) =>
                        setInvestment({
                          ...investment,
                          descricao: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Valor do período
                    <input
                      type="number"
                      min="0"
                      value={investment.valor}
                      onChange={(e) =>
                        setInvestment({ ...investment, valor: e.target.value })
                      }
                    />
                  </label>
                  <label>
                    Origem
                    <select
                      value={investment.originId}
                      onChange={(e) =>
                        setInvestment({
                          ...investment,
                          originId: e.target.value,
                        })
                      }
                    >
                      <option value="">Legado sem vínculo</option>
                      {(catalog.origins || []).map((origin) => (
                        <option key={origin.id} value={origin.id}>
                          #{origin.id} · {origin.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Rateio
                    <select
                      value={investment.allocationMethod}
                      onChange={(e) =>
                        setInvestment({
                          ...investment,
                          allocationMethod: e.target.value,
                        })
                      }
                    >
                      <option value="period_prorated">
                        Proporcional aos dias
                      </option>
                      <option value="full_period">
                        Valor integral se sobrepor
                      </option>
                      <option value="monthly">Valor mensal proporcional</option>
                    </select>
                  </label>
                  <label>
                    Início
                    <input
                      type="date"
                      value={investment.dataInicial}
                      onChange={(e) =>
                        setInvestment({
                          ...investment,
                          dataInicial: e.target.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    Fim
                    <input
                      type="date"
                      value={investment.dataFinal}
                      onChange={(e) =>
                        setInvestment({
                          ...investment,
                          dataFinal: e.target.value,
                        })
                      }
                    />
                  </label>
                  {investment.allocationMethod === "monthly" && (
                    <label>
                      Valor mensal
                      <input
                        type="number"
                        min="0"
                        value={investment.monthlyAmount}
                        onChange={(e) =>
                          setInvestment({
                            ...investment,
                            monthlyAmount: e.target.value,
                          })
                        }
                      />
                    </label>
                  )}
                  <button onClick={saveInvestment}>
                    Adicionar investimento
                  </button>
                </div>
                <div className="psai-table">
                  {(catalog.investments || []).map((item) => (
                    <div key={item.id}>
                      <span>
                        {item.descricao}
                        <small>
                          {item.data_inicial} a {item.data_final}
                        </small>
                      </span>
                      <span>
                        R${" "}
                        {Number(item.valor || 0).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                      <b>
                        {item.origin_id
                          ? `Origem #${item.origin_id}`
                          : "Vínculo pendente"}
                      </b>
                      <button
                        className="danger"
                        onClick={() => deleteInvestment(item.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  ))}
                </div>
                {!(catalog.investments || []).length && (
                  <p className="note">
                    Não há investimentos: custo por oportunidade e ROI
                    permanecem indisponíveis.
                  </p>
                )}
              </article>
            )}
            {historySteps.includes(step) && (
              <article className="psai-panel">
                <h3>Auditoria somente leitura</h3>
                <div className="psai-metrics">
                  {[
                    ["cards", diagnostic.summary?.cards],
                    ["históricos", diagnostic.summary?.history_records],
                    [
                      "transições",
                      diagnostic.summary?.recognizable_transitions,
                    ],
                    [
                      "renomeadas/ausentes",
                      diagnostic.transition_resolution?.renamed_or_missing,
                    ],
                    [
                      "duplicidades",
                      diagnostic.integrity?.duplicate_signatures,
                    ],
                    [
                      "WhatsApp vinculado",
                      diagnostic.structured_sources?.linked_whatsapp_messages,
                    ],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <b>{value || 0}</b>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <p className="note">
                  A coluna anterior fica nula sem prova. Tarefa concluída não é
                  conversa confirmada. A coluna atual não prova o caminho.
                </p>
                <div className="psai-actions">
                  <button
                    disabled={Boolean(busy) || runIsActive}
                    onClick={() => createRun(true)}
                  >
                    {busy === "create-dry-run"
                      ? "Criando dry-run..."
                      : "Criar dry-run"}
                  </button>
                  <button
                    disabled={Boolean(busy) || runIsActive}
                    onClick={() => createRun(false)}
                  >
                    {busy === "create-backfill"
                      ? "Criando backfill..."
                      : "Criar backfill real"}
                  </button>
                  <button
                    className="secondary"
                    disabled={Boolean(busy) || !run}
                    onClick={loadIssues}
                  >
                    Ambiguidades
                  </button>
                </div>
                {run && (
                  <div className="psai-run" aria-busy={busy === "process"}>
                    <b>
                      Execução #{run.id} · {run.status}
                    </b>
                    <progress max={runTotal} value={runAnalyzed} />
                    <small>
                      {run.analyzed_count || 0} analisados ·{" "}
                      {run.inserted_count || 0} inseridos ·{" "}
                      {run.ambiguous_count || 0} ambíguos
                    </small>
                    <span>{runPercentage.toFixed(1)}% concluído</span>
                    {busy === "process" && (
                      <strong className="psai-run-status">
                        Processando lotes... mantenha esta página aberta.
                      </strong>
                    )}
                    {run.status === "failed" && run.error_message && (
                      <p className="psai-run-error">{run.error_message}</p>
                    )}
                    <div>
                      {run.status === "failed" && (
                        <button
                          disabled={Boolean(busy)}
                          onClick={resumeRun}
                        >
                          {busy === "resume-run"
                            ? "Retomando..."
                            : "Retomar execução"}
                        </button>
                      )}
                      {runIsActive && (
                        <button
                          disabled={Boolean(busy)}
                          onClick={processRun}
                        >
                          {busy === "process"
                            ? "Processando..."
                            : "Processar lotes"}
                        </button>
                      )}
                      {busy === "process" && (
                        <button
                          className="danger"
                          onClick={() => {
                            stop.current = true;
                          }}
                        >
                          Pausar após lote
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {issues.map((issue) => (
                  <div className="psai-issue" key={issue.id}>
                    <b>{issue.reason_code}</b>
                    <span>Card #{issue.card_id}</span>
                    <p>{issue.raw_text}</p>
                  </div>
                ))}
              </article>
            )}
            {step === "activation" && (
              <article className="psai-panel">
                <h3>Checklist final</h3>
                <div className="psai-checks">
                  {checks.map((item) => (
                    <div
                      key={item.check_code}
                      className={`${item.result} ${item.severity}`}
                    >
                      <b>{item.title}</b>
                      <span>{item.readiness_state}</span>
                      <p>{item.description}</p>
                      <small>{item.remediation}</small>
                    </div>
                  ))}
                </div>
                <div className="psai-actions">
                  <button onClick={setCapture}>
                    {settings.capture_enabled
                      ? "Desabilitar captura"
                      : "Habilitar shadow-write"}
                  </button>
                  <button
                    disabled={critical.length > 0 || !settings.capture_enabled}
                    onClick={activate}
                  >
                    Ativar SyncsAI
                  </button>
                </div>
                <p className="note">
                  Rollback: desligue as flags. Evidências e auditoria ficam
                  preservadas.
                </p>
              </article>
            )}
            <article className="psai-panel">
              <div className="psai-title">
                <div>
                  <h3>Prévia e versão</h3>
                  <p>
                    Salvar cria um rascunho auditável. Publicar só fica
                    disponível quando não houver alterações locais.
                  </p>
                </div>
                <span
                  className={
                    hasUnsavedChanges
                      ? "psai-save-state dirty"
                      : "psai-save-state"
                  }
                >
                  {hasUnsavedChanges ? "Não salvo" : "Salvo"}
                </span>
              </div>
              <div className="psai-version">
                <label>
                  Vigência
                  <input
                    type="datetime-local"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                  />
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={retroactive}
                    onChange={(e) => setRetroactive(e.target.checked)}
                  />{" "}
                  Autorizar aplicação retroativa
                </label>
                <label>
                  Justificativa
                  <textarea
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                  />
                </label>
              </div>
              <div className="psai-actions">
                <button
                  className="secondary"
                  disabled={Boolean(busy)}
                  onClick={previewConfig}
                >
                  Gerar prévia
                </button>
                <button
                  disabled={Boolean(busy)}
                  onClick={save}
                >
                  {busy === "save" ? "Validando e salvando…" : "Salvar versão"}
                </button>
                <button
                  disabled={Boolean(busy) || hasUnsavedChanges}
                  onClick={publish}
                >
                  Publicar rascunho
                </button>
              </div>
              {preview && (
                <div
                  className={
                    preview.valid
                      ? "psai-preview valid"
                      : "psai-preview invalid"
                  }
                >
                  <b>{preview.valid ? "Válida" : "Bloqueada"}</b>
                  <span>
                    {preview.preview?.affected_current_cards || 0} cards
                    afetados
                  </span>
                  {(preview.problems || []).map((item) => (
                    <p key={item.code}>
                      {item.code} — {item.description}
                    </p>
                  ))}
                </div>
              )}
            </article>
          </section>
        </div>
      </main>
    </>
  );
}
